import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from 'react';

import type { KnowledgeEntryKindEnum } from '~/data/generated/model';
import type { KnowledgeTarget } from '~/data/hooks/useKnowledgeTargets';

import { AddKnowledgeDialog } from './AddKnowledgeDialog';
import { useKnowledgeEnabled } from './useKnowledgeEnabled';

interface OpenOptions {
  target?: KnowledgeTarget;
  content?: string;
  kind?: KnowledgeEntryKindEnum;
}

interface KnowledgeCaptureContextValue {
  openAddKnowledge: (options?: OpenOptions) => void;
}

const KnowledgeCaptureContext = createContext<KnowledgeCaptureContextValue | null>(null);

export function KnowledgeCaptureProvider({ children }: { children: ReactNode }) {
  const enabled = useKnowledgeEnabled();
  const [open, setOpen] = useState<(OpenOptions & { key: number }) | null>(null);

  const openAddKnowledge = useCallback((options: OpenOptions = {}) => {
    setOpen({ ...options, key: Date.now() });
  }, []);

  const value = useMemo(() => (enabled ? { openAddKnowledge } : null), [enabled, openAddKnowledge]);

  return (
    <KnowledgeCaptureContext.Provider value={value}>
      {children}
      {enabled && open ? (
        <AddKnowledgeDialog
          key={open.key}
          initialTarget={open.target}
          initialContent={open.content}
          initialKind={open.kind}
          onClose={() => setOpen(null)}
        />
      ) : null}
    </KnowledgeCaptureContext.Provider>
  );
}

/** `null` for a user who can't capture knowledge, which hides every surface. */
export function useKnowledgeCapture(): KnowledgeCaptureContextValue | null {
  return useContext(KnowledgeCaptureContext);
}
